import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import LogoutIcon from '@mui/icons-material/Logout';
import axios from 'axios';
import { API_BASE_URL } from '../config/config.js';
import { useAuth } from '../context/Auth';

import ManageAds from './ManageAds';
import Special from './Special';
import Accessibility from './Accessibility';
import ManageAirlinesEnhanced from './ManageAirlinesEnhanced';
import FleetManagement from './fleet/FleetManagement';
import ObservabilityPanel from './observability/ObservabilityPanel';
import BaggageGateEditor from './BaggageGateEditor';
import StatusOverrideEditor from './StatusOverrideEditor';
import UserManagement from './UserManagement';
import IncidentReporting from './IncidentReporting';

// All available tabs with their IDs matching the database
const ALL_TABS = [
    { id: 'manage-ads', label: 'Manage Ads', component: ManageAds },
    { id: 'special-announcements', label: 'Special Announcements', component: Special },
    { id: 'accessibility', label: 'Accessibility', component: Accessibility },
    { id: 'airlines', label: 'Airlines', component: ManageAirlinesEnhanced },
    { id: 'fleet', label: 'Fleet', component: FleetManagement },
    { id: 'observability', label: 'Observability', component: ObservabilityPanel },
    { id: 'baggage-gate', label: 'Baggage & Gate', component: BaggageGateEditor },
    { id: 'status-override', label: 'STATUS', component: StatusOverrideEditor },
    { id: 'incidents', label: 'INCIDENTS', component: IncidentReporting },
    { id: 'user-management', label: 'User Management', component: UserManagement },
];

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export default function AdminPage() {
    const [value, setValue] = useState(0);
    const [allowedTabs, setAllowedTabs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userEmail, setUserEmail] = useState('');
    const [, setAuth] = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadAllowedTabs();
    }, []);

    const loadAllowedTabs = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get auth token from localStorage
            const authData = localStorage.getItem('auth');
            if (!authData) {
                setError('Not authenticated');
                setLoading(false);
                return;
            }

            const parsed = JSON.parse(authData);
            const token = parsed.token;
            const userRole = parsed.data?.role;
            setUserEmail(parsed.data?.email || '');

            // Admin always gets all tabs
            if (userRole === 'admin') {
                setAllowedTabs(ALL_TABS.map(t => t.id));
                setLoading(false);
                return;
            }

            // Fetch allowed tabs from API
            const response = await axios.get(`${API_BASE_URL}/api/admin/role-permissions/my-tabs`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                setAllowedTabs(response.data.data.allowedTabs || []);
            } else {
                setError(response.data.message || 'Failed to load permissions');
            }
        } catch (err) {
            console.error('Error loading tab permissions:', err);
            // On error, fall back to showing no tabs (or could show default tabs)
            setError(err.response?.data?.message || 'Failed to load permissions');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleLogout = () => {
        localStorage.removeItem('auth');
        setAuth({ user: null, token: '', role: null });
        navigate('/login');
    };

    // Filter tabs based on permissions
    const visibleTabs = ALL_TABS.filter(tab => allowedTabs.includes(tab.id));

    // Header bar with logout - shown on all screens
    const HeaderBar = () => (
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: '#f5f5f5'
        }}>
            <Typography variant="h6" sx={{ color: '#000' }}>
                FIDS Admin
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {userEmail && (
                    <Typography variant="body2" sx={{ color: '#666' }}>
                        {userEmail}
                    </Typography>
                )}
                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                >
                    Logout
                </Button>
            </Box>
        </Box>
    );

    if (loading) {
        return (
            <Box sx={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: '#fff'
            }}>
                <HeaderBar />
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 'calc(100vh - 70px)'
                }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: '#fff'
            }}>
                <HeaderBar />
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    minHeight: 'calc(100vh - 70px)'
                }}>
                    <Typography color="error">{error}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please try logging in again.
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (visibleTabs.length === 0) {
        return (
            <Box sx={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: '#fff'
            }}>
                <HeaderBar />
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    minHeight: 'calc(100vh - 70px)'
                }}>
                    <Typography variant="h6" sx={{ color: '#000' }}>No Access</Typography>
                    <Typography variant="body2" color="text.secondary">
                        You don't have permission to access any admin features.
                        Please contact an administrator.
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#fff', color: '#000', pb: 4 }}>
            <HeaderBar />
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="admin tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {visibleTabs.map((tab, index) => (
                        <Tab key={tab.id} label={tab.label} {...a11yProps(index)} />
                    ))}
                </Tabs>
            </Box>
            {visibleTabs.map((tab, index) => {
                const TabComponent = tab.component;
                return (
                    <TabPanel key={tab.id} value={value} index={index}>
                        <TabComponent />
                    </TabPanel>
                );
            })}
        </Box>
    );
}
